import { useMessage } from '@/hooks/useMessage';
import { IProvided } from '@/node-editor/context';
import { INodeAction } from '@/node-editor/store';
import { ConferenceContext } from '@/store';
import { debounce } from '@mui/material';
import React, { Fragment, useCallback, useContext, useRef } from 'react';

interface ContentWindowProps extends IProvided {
  slug: string;
  children: React.ReactNode;
}

export const ContentWindow: React.FC<ContentWindowProps> = ({
  slug,
  context,
  dispatch,
  children,
}) => {
  const pageSlug = useRef(slug);
  const lastContentRef = useRef<string | null>(null);

  // Conference-level data (e.g. registration_fields) lives in ConferenceContext,
  // not the node store, so CONFERENCE_PATCH messages go through its patch action.
  const { actions: conferenceActions } = useContext(ConferenceContext) ?? {};

  // Memoize debounced function to prevent recreation
  const debounceCurrent = useCallback(debounce(dispatch, 200), [dispatch]);

  // Optimized message handler to prevent unnecessary re-renders
  const handleMessage = useCallback(
    (_send: any, data: INodeAction, origin: string) => {
      if (context !== 'Render') {
        return;
      }

      if (pageSlug.current !== data.payload.slug) {
        return;
      }

      if (!origin.includes('coeo.events')) {
        return;
      }

      const { slug, ...action } = data.payload;

      switch (action.type) {
        case 'NODES_RESET': {
          // Prevent unnecessary content updates if content hasn't changed
          const contentString = JSON.stringify(action.payload);
          if (lastContentRef.current === contentString) {
            console.log('🔄 Content unchanged, skipping NODES_RESET');
            return;
          }
          lastContentRef.current = contentString;
          console.log('🔄 Processing NODES_RESET with new content');
          return dispatch({ ...action });
        }
        case 'CURRENT_NODE': {
          // Only process CURRENT_NODE if payload is not empty to prevent unnecessary updates
          if (action.payload && action.payload !== '') {
            return debounceCurrent(action);
          }
          return;
        }
        // Live conference-level edits from the page editor (e.g.
        // registration_fields). Patched into ConferenceContext in place so the
        // preview updates instantly, mirroring the node NODES_RESET flow.
        case 'CONFERENCE_PATCH': {
          if (conferenceActions?.patchConferenceDetail && action.payload) {
            conferenceActions.patchConferenceDetail(action.payload);
          }
          return;
        }
      }
    },
    [context, dispatch, debounceCurrent, conferenceActions]
  );

  useMessage<INodeAction>('preview_window', handleMessage);

  return <Fragment>{children}</Fragment>;
};
